import React, { Suspense, lazy, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Box, CircularProgress, Fab, IconButton, Paper, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import useAppTranslation from '../../hooks/useAppTranslation';
import { MSME_MOBILE_BOTTOM_NAV_HEIGHT } from '../MsmeMobileBottomNav';

const CarbonCopilotPage = lazy(() => import('../CarbonCopilotPage'));

/** Default MUI Fab size. The shell scroll padding uses the same value. */
export const AI_ADVISOR_FAB_SIZE_PX = 56;
/** Space between the bottom nav and the Fab, also used above the Fab. */
export const AI_ADVISOR_FAB_GAP_PX = 16;
/** Fab offset once the bottom nav is gone (md and up). */
export const AI_ADVISOR_FAB_BOTTOM_DESKTOP_PX = 24;

/**
 * Floating AI Advisor. The chat stays mounted after the first open so the thread
 * survives while the user moves between workspace pages.
 */
const AiAdvisorLauncher: React.FC = () => {
  const { t } = useAppTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const advisorRequested = new URLSearchParams(location.search).get('advisor') === '1';

  useEffect(() => {
    if (advisorRequested) {
      setOpen(true);
      setMounted(true);
    }
  }, [advisorRequested]);

  const openChat = () => {
    setMounted(true);
    setOpen(true);
  };

  const closeChat = () => {
    setOpen(false);
    if (!advisorRequested) {
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete('advisor');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  };

  return (
    <>
      <Paper
        elevation={8}
        role="dialog"
        aria-label={t('copilot.title')}
        aria-hidden={!open}
        sx={{
          display: open ? 'flex' : 'none',
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          right: { xs: 12, sm: 24 },
          bottom: {
            xs: `calc(${MSME_MOBILE_BOTTOM_NAV_HEIGHT}px + ${AI_ADVISOR_FAB_SIZE_PX + AI_ADVISOR_FAB_GAP_PX}px + env(safe-area-inset-bottom, 0px))`,
            md: 88,
          },
          width: { xs: 'min(100vw - 24px, 420px)', sm: 400 },
          height: { xs: 'min(70dvh, 560px)', md: 560 },
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'primary.dark',
            color: 'primary.contrastText',
          }}
        >
          <SmartToyOutlinedIcon fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            {t('copilot.title')}
          </Typography>
          <IconButton
            size="small"
            aria-label="Close AI Advisor"
            onClick={closeChat}
            sx={{ color: 'inherit' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {mounted ? (
            <Suspense
              fallback={(
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
            >
              <CarbonCopilotPage embedded />
            </Suspense>
          ) : null}
        </Box>
      </Paper>

      <Fab
        color="primary"
        aria-label={open ? 'Close AI Advisor' : 'Open AI Advisor'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={open ? closeChat : openChat}
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          right: { xs: 16, sm: 24 },
          bottom: {
            xs: `calc(${MSME_MOBILE_BOTTOM_NAV_HEIGHT}px + ${AI_ADVISOR_FAB_GAP_PX}px + env(safe-area-inset-bottom, 0px))`,
            md: AI_ADVISOR_FAB_BOTTOM_DESKTOP_PX,
          },
        }}
      >
        {open ? <CloseIcon /> : <SmartToyOutlinedIcon />}
      </Fab>
    </>
  );
};

export default AiAdvisorLauncher;
